import { apiClient } from './client';
import type { PositionTitle, PositionTitleWrite } from '@/types/oscar';

/**
 * Typed wrappers over PR-26's `/api/onboarding/position-titles*` endpoints.
 * Reads open to any authenticated Oscar user; create / re-home / retire are
 * admin-only (enforced server-side).
 */

/** GET /api/onboarding/position-titles — list (optionally scoped to one department). */
export function listPositionTitles(departmentCode?: string, signal?: AbortSignal): Promise<PositionTitle[]> {
  const qs = departmentCode ? `?departmentCode=${encodeURIComponent(departmentCode)}` : '';
  return apiClient.get<PositionTitle[]>(`/api/onboarding/position-titles${qs}`, signal);
}

/** GET including retired (soft-deleted) titles — for the Restore tray. */
export function listPositionTitlesWithRetired(signal?: AbortSignal): Promise<PositionTitle[]> {
  return apiClient.get<PositionTitle[]>('/api/onboarding/position-titles?includeDeleted=true', signal);
}

/** POST /api/onboarding/position-titles — create (admin). */
export function createPositionTitle(body: PositionTitleWrite): Promise<PositionTitle> {
  return apiClient.post<PositionTitle>('/api/onboarding/position-titles', body);
}

/** PATCH /api/onboarding/position-titles/:id — rename and/or re-home (admin). */
export function updatePositionTitle(id: number, body: PositionTitleWrite): Promise<PositionTitle> {
  return apiClient.patch<PositionTitle>(`/api/onboarding/position-titles/${id}`, body);
}

/** POST /api/onboarding/position-titles/:id/retire — soft delete (admin). */
export function retirePositionTitle(id: number): Promise<{ PositionTitleId: number; IsDeleted: boolean }> {
  return apiClient.post<{ PositionTitleId: number; IsDeleted: boolean }>(`/api/onboarding/position-titles/${id}/retire`);
}

/** POST /api/onboarding/position-titles/:id/unassign — clear the department (admin). Non-destructive. */
export function unassignPositionTitle(id: number): Promise<PositionTitle> {
  return apiClient.post<PositionTitle>(`/api/onboarding/position-titles/${id}/unassign`);
}

/** POST /api/onboarding/position-titles/:id/restore — un-retire a soft-deleted title (admin). */
export function restorePositionTitle(id: number): Promise<PositionTitle> {
  return apiClient.post<PositionTitle>(`/api/onboarding/position-titles/${id}/restore`);
}
